import { APP_INITIALIZER, NgModule } from "@angular/core";
import { BrowserModule, Title } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { RouterModule } from "@angular/router";
import { HELPDESK_TOOLKIT_ENDPOINTS, HelpdeskToolkitModule } from "@dtm-frontend/helpdesk-toolkit-lib";
import { KEYCLOAK_CONFIG, SharedAuthModule } from "@dtm-frontend/shared/auth";
import { SharedUiModule } from "@dtm-frontend/shared/ui";
import {
    I18nRootModule,
    LANGUAGE_CONFIGURATION,
    LanguageCode,
    TranslationHelperService,
    getTranslocoInlineLoader,
} from "@dtm-frontend/shared/ui/i18n";
import { FunctionUtils, Logger, LoggerModule } from "@dtm-frontend/shared/utils";
import { TRANSLOCO_SCOPE } from "@jsverse/transloco";
import { NgxsReduxDevtoolsPluginModule } from "@ngxs/devtools-plugin";
import { NgxsStoragePluginModule, StorageOption } from "@ngxs/storage-plugin";
import { NgxsModule } from "@ngxs/store";
import { first, tap } from "rxjs/operators";
import { HelpdeskToolkitEnvironment } from "../environments/environment.model";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { HeaderComponent } from "./components/header/header.component";
import { ActionsHandler } from "./services/actions.handler";

Logger.initialize("https://c394bd502116555435acc6ab6e9cc3e7@sentry.pansa.cloud/18");

export function createAppModule(environment: HelpdeskToolkitEnvironment) {
    @NgModule({
        declarations: [AppComponent, HeaderComponent],
        imports: [
            NgxsModule.forRoot([], {
                // NOTE: must be first because of https://github.com/ngxs/store/issues/375
                developmentMode: !environment.production,
            }),
            AppRoutingModule,
            RouterModule,
            BrowserAnimationsModule,
            BrowserModule,
            I18nRootModule.forRoot({
                developmentMode: !environment.production,
                ...LANGUAGE_CONFIGURATION,
            }),
            NgxsStoragePluginModule.forRoot({
                key: ["ui"],
                storage: StorageOption.LocalStorage,
            }),
            NgxsReduxDevtoolsPluginModule.forRoot({
                disabled: environment.production,
                name: "Helpdesk Toolkit",
            }),
            LoggerModule.forRoot(environment.name, !environment.production),
            SharedAuthModule.forRoot({
                bearerExcludedUrls: [...environment.bearerExcludedUrls],
            }),
            SharedUiModule,
            HelpdeskToolkitModule.forRoot(),
        ],
        providers: [
            {
                provide: APP_INITIALIZER,
                useFactory: () => FunctionUtils.noop,
                deps: [ActionsHandler],
                multi: true,
            },
            {
                provide: TRANSLOCO_SCOPE,
                multi: true,
                useValue: {
                    scope: "helpdeskToolkit",
                    loader: getTranslocoInlineLoader((language: LanguageCode) => import(`../assets/i18n/${language}.json`)),
                },
            },
            {
                provide: KEYCLOAK_CONFIG,
                useValue: environment.keycloakConfig,
            },
            {
                provide: HELPDESK_TOOLKIT_ENDPOINTS,
                useValue: environment.helpdeskToolkitEndpoints,
            },
        ],
        bootstrap: [AppComponent],
    })
    class AppModule {
        constructor(translocoHelper: TranslationHelperService, titleService: Title) {
            translocoHelper
                .waitForTranslation("helpdeskToolkit.appTitle")
                .pipe(first(), tap(titleService.setTitle.bind(titleService)))
                .subscribe();
        }
    }

    return AppModule;
}
